import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "agentMarkdown",
    "agentMessage",
    "agenticPanel",
    "agenticTab",
    "emptyState",
    "manualPanel",
    "manualTab",
    "optionSelect",
    "payload",
    "status",
    "strategyDescription",
    "strategyName",
    "strategyStatus",
    "stepList",
    "submit",
    "validation"
  ]

  static values = {
    initialStrategy: Object,
    options: Object
  }

  connect() {
    this.nextStepId = 1
    this.draggedStepUid = null
    this.pointerDrag = null
    this.boundPointerMove = this.pointerMove.bind(this)
    this.boundPointerUp = this.pointerUp.bind(this)

    this.populateOptionSelect()
    this.steps = this.initialSteps()
    this.showBuilderTab("agentic")
    this.render()
  }

  disconnect() {
    this.clearPointerDrag()
  }

  switchBuilderTab(event) {
    event.preventDefault()
    this.showBuilderTab(event.params.tab || "agentic")
  }

  addStep(event) {
    event.preventDefault()

    const option = this.optionById(this.optionSelectTarget.value)
    if (!option) return

    this.steps.push(this.buildStep(option))
    this.render()
  }

  buildFromAgentMarkdown(event) {
    event.preventDefault()

    this.buildStrategyFromAgentMarkdown()
  }

  buildStrategyFromAgentMarkdown() {
    try {
      const strategy = this.strategyFromAgentMarkdown(this.agentMarkdownTarget.value)
      const steps = this.stepsFromPayload(strategy.payload, true)

      if (steps.length === 0) {
        throw new Error("The agent output did not include any strategy rules.")
      }

      this.steps = steps
      this.applyStrategyMetadata(strategy)
      this.render()
      this.showAgentMessage(`Built ${steps.length} rule${steps.length === 1 ? "" : "s"} from agent output.`, "success")
      return true
    } catch (error) {
      this.showAgentMessage(error.message, "error")
      return false
    }
  }

  duplicateStep(event) {
    event.preventDefault()

    const step = this.stepFromEvent(event)
    if (!step) return

    const copy = JSON.parse(JSON.stringify(step))
    copy.uid = this.newStepUid()
    copy.name = `${step.name} Copy`
    this.steps.splice(this.stepIndex(step.uid) + 1, 0, copy)
    this.render()
  }

  removeStep(event) {
    event.preventDefault()

    const step = this.stepFromEvent(event)
    if (!step) return

    this.steps = this.steps.filter((candidate) => candidate.uid !== step.uid)
    this.render()
  }

  moveUp(event) {
    event.preventDefault()
    this.moveStep(event, -1)
  }

  moveDown(event) {
    event.preventDefault()
    this.moveStep(event, 1)
  }

  dragStart(event) {
    const stepElement = event.currentTarget.closest("[data-step-uid]")
    this.draggedStepUid = stepElement?.dataset.stepUid
    if (!this.draggedStepUid) return

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", this.draggedStepUid)
    this.markDraggingStep(this.draggedStepUid)
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    this.moveDraggingStepForEvent(event, event.currentTarget)
  }

  drop(event) {
    event.preventDefault()

    this.syncStepsFromDomOrder()
    this.draggedStepUid = null
    this.render()
  }

  dragEnd() {
    const hadDraggedStep = Boolean(this.draggedStepUid)
    if (hadDraggedStep) {
      this.syncStepsFromDomOrder()
    }

    this.draggedStepUid = null
    this.markDraggingStep(null)
    if (hadDraggedStep) this.render()
  }

  pointerStart(event) {
    if (event.button !== undefined && event.button !== 0) return

    const stepElement = event.currentTarget.closest("[data-step-uid]")
    if (!stepElement) return

    event.preventDefault()
    this.pointerDrag = {
      uid: stepElement.dataset.stepUid
    }
    this.draggedStepUid = this.pointerDrag.uid
    this.markDraggingStep(this.pointerDrag.uid)
    window.addEventListener("pointermove", this.boundPointerMove)
    window.addEventListener("pointerup", this.boundPointerUp, { once: true })
    window.addEventListener("pointercancel", this.boundPointerUp, { once: true })
  }

  pointerMove(event) {
    if (!this.pointerDrag) return

    const draggingElement = this.draggingStepElement()
    if (draggingElement) draggingElement.style.pointerEvents = "none"
    const targetStep = document.elementFromPoint(event.clientX, event.clientY)?.closest("[data-step-uid]")
    if (draggingElement) draggingElement.style.pointerEvents = ""
    if (!targetStep || !this.stepListTarget.contains(targetStep)) return

    this.moveDraggingStepForEvent(event, targetStep)
  }

  pointerUp() {
    if (this.pointerDrag) {
      this.syncStepsFromDomOrder()
    }

    this.clearPointerDrag()
    this.render()
  }

  toggleEnabled(event) {
    const step = this.stepFromEvent(event)
    if (!step) return

    step.enabled = event.currentTarget.checked
    this.render()
  }

  updateNumber(event) {
    const step = this.stepFromEvent(event)
    if (!step) return

    step.values[event.currentTarget.dataset.fieldId] = event.currentTarget.value
    this.updatePayloadAndValidation()
  }

  updateGradeMix(event) {
    const step = this.stepFromEvent(event)
    if (!step) return

    step.values.targets[event.currentTarget.dataset.grade] = event.currentTarget.value
    this.updatePayloadAndValidation()
    this.refreshGradeTotals()
  }

  toggleGrade(event) {
    const step = this.stepFromEvent(event)
    if (!step) return

    const fieldId = event.currentTarget.dataset.fieldId
    const grade = event.currentTarget.dataset.grade
    const grades = step.values[fieldId] || []

    if (event.currentTarget.checked && !grades.includes(grade)) {
      grades.push(grade)
    } else if (!event.currentTarget.checked) {
      step.values[fieldId] = grades.filter((candidate) => candidate !== grade)
    }

    this.updatePayloadAndValidation()
    this.refreshGradeOrders()
  }

  addThreshold(event) {
    event.preventDefault()

    const step = this.stepFromEvent(event)
    if (!step) return

    const fieldId = event.currentTarget.dataset.thresholdField || "thresholds"
    const thresholds = step.values[fieldId] || {}
    const nextSkuCount = Math.max(0, ...Object.keys(thresholds).map((key) => parseInt(key, 10) || 0)) + 1
    const nextRoundCount = Math.max(0, ...Object.values(thresholds).map((value) => parseFloat(value) || 0)) + 100
    thresholds[String(nextSkuCount)] = nextRoundCount
    step.values[fieldId] = thresholds
    this.render()
  }

  removeThreshold(event) {
    event.preventDefault()

    const step = this.stepFromEvent(event)
    if (!step) return

    const fieldId = event.currentTarget.dataset.thresholdField || "thresholds"
    delete step.values[fieldId][event.currentTarget.dataset.thresholdKey]
    this.render()
  }

  updateThreshold(event) {
    const step = this.stepFromEvent(event)
    if (!step) return

    const fieldId = event.currentTarget.dataset.thresholdField || "thresholds"
    const thresholds = step.values[fieldId] || {}
    const oldKey = event.currentTarget.dataset.thresholdKey

    if (event.currentTarget.dataset.thresholdPart === "sku_count") {
      const value = thresholds[oldKey]
      const newKey = String(Math.max(1, parseInt(event.currentTarget.value, 10) || 1))
      delete thresholds[oldKey]
      thresholds[newKey] = value
    } else {
      thresholds[oldKey] = event.currentTarget.value
    }

    step.values[fieldId] = thresholds
    this.updatePayloadAndValidation()
  }

  validateBeforeSubmit(event) {
    if (this.steps.length === 0 && this.hasAgentMarkdownTarget && this.agentMarkdownTarget.value.trim().length > 0) {
      this.buildStrategyFromAgentMarkdown()
    }

    this.updatePayloadAndValidation()

    if (this.currentValidationMessages().length > 0) {
      event.preventDefault()
    }
  }

  populateOptionSelect() {
    const options = this.availableOptions()
    this.optionSelectTarget.innerHTML = options.map((option) => {
      return `<option value="${this.escapeHtml(option.id)}">${this.escapeHtml(option.name)}</option>`
    }).join("")
  }

  render() {
    this.stepListTarget.innerHTML = this.steps.map((step, index) => this.renderStep(step, index)).join("")
    this.emptyStateTarget.classList.toggle("hidden", this.steps.length > 0)
    this.statusTarget.textContent = `${this.steps.length} rule${this.steps.length === 1 ? "" : "s"}`
    this.updatePayloadAndValidation()
    this.refreshGradeTotals()
    this.refreshGradeOrders()
  }

  showBuilderTab(tab) {
    const agentic = tab === "agentic"

    if (this.hasManualPanelTarget) this.manualPanelTarget.classList.toggle("hidden", agentic)
    if (this.hasAgenticPanelTarget) this.agenticPanelTarget.classList.toggle("hidden", !agentic)

    if (this.hasManualTabTarget) this.styleBuilderTab(this.manualTabTarget, !agentic)
    if (this.hasAgenticTabTarget) this.styleBuilderTab(this.agenticTabTarget, agentic)
  }

  styleBuilderTab(tabTarget, active) {
    if (!tabTarget) return

    tabTarget.setAttribute("aria-selected", active ? "true" : "false")
    tabTarget.className = active ?
      "rounded-lg bg-black px-5 py-3 text-base font-black uppercase text-white shadow-sm transition hover:opacity-90" :
      "rounded-lg bg-transparent px-5 py-3 text-base font-black uppercase text-gray-600 transition hover:bg-white"
  }

  renderStep(step, index) {
    const mutedClass = step.enabled ? "" : " opacity-60"

    return `
      <article
        class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition${mutedClass}"
        data-step-uid="${this.escapeHtml(step.uid)}"
        data-action="dragover->pick-lab-strategy-builder#dragOver drop->pick-lab-strategy-builder#drop"
      >
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div class="flex min-w-0 flex-wrap items-center gap-2">
              <button
                type="button"
                class="inline-flex h-8 w-8 touch-none items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
                title="Drag to reorder"
                aria-label="Drag to reorder rule"
                draggable="true"
                data-action="pointerdown->pick-lab-strategy-builder#pointerStart dragstart->pick-lab-strategy-builder#dragStart dragend->pick-lab-strategy-builder#dragEnd"
              >
                ${this.gripIcon()}
              </button>
              <span class="rounded-full bg-gray-950 px-3 py-1 text-xs font-black uppercase tracking-widest text-white">Rule ${index + 1}</span>
              ${step.enabled ? "" : '<span class="rounded-full bg-gray-200 px-3 py-1 text-xs font-black uppercase tracking-widest text-gray-700">Disabled</span>'}
          </div>
          <div class="grid shrink-0 grid-cols-2 gap-2 sm:flex sm:max-w-md sm:flex-wrap sm:justify-end">
            <button type="button" class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest text-gray-900 hover:bg-gray-50" data-action="pick-lab-strategy-builder#moveUp">Up</button>
            <button type="button" class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest text-gray-900 hover:bg-gray-50" data-action="pick-lab-strategy-builder#moveDown">Down</button>
            <button
              type="button"
              class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest text-gray-900 hover:bg-gray-50"
              data-action="pick-lab-strategy-builder#duplicateStep"
            >Duplicate</button>
            <button type="button" class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold uppercase tracking-widest text-red-700 hover:bg-red-100" data-action="pick-lab-strategy-builder#removeStep">Remove</button>
          </div>
        </div>
        <h5 class="mt-3 text-lg font-black text-gray-950">${this.escapeHtml(step.name)}</h5>
        <p class="mt-1 text-sm leading-6 text-gray-600">${this.escapeHtml(step.description)}</p>
        <div class="mt-4 border-t border-gray-200 pt-4">
          <label class="inline-flex items-center rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-bold text-gray-800">
            <input type="checkbox" class="mr-2 h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500" ${step.enabled ? "checked" : ""} data-action="pick-lab-strategy-builder#toggleEnabled">
            Enabled
          </label>
          ${this.renderFields(step)}
        </div>
      </article>
    `
  }

  renderFields(step) {
    const fields = (step.option.fields || []).filter((field) => field.id !== "enabled")
    if (fields.length === 0) {
      return '<p class="mt-4 rounded-xl bg-gray-50 p-4 text-sm text-gray-600">This rule uses its enabled toggle only.</p>'
    }

    if (step.type === "grade_mix") {
      return `
        <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          ${fields.map((field) => this.renderGradeMixField(step, field)).join("")}
        </div>
        <p class="mt-3 rounded-xl bg-gray-50 p-3 text-sm font-bold text-gray-700" data-grade-total="${this.escapeHtml(step.uid)}"></p>
      `
    }

    return `<div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">${fields.map((field) => {
      if (field.control === "threshold_table") return this.renderThresholdTable(step, field)
      if (field.control === "multiselect" || field.control === "ordered_multiselect") {
        return this.renderGradeSelect(step, field)
      }

      return this.renderNumberField(step, field)
    }).join("")}</div>`
  }

  renderGradeMixField(step, field) {
    const value = step.values.targets[field.id] ?? field.default

    return `
      <label class="block">
        <span class="block text-xs font-bold uppercase tracking-widest text-gray-700">${this.escapeHtml(field.label)}</span>
        <input
          type="number"
          min="0"
          max="100"
          step="1"
          value="${this.escapeHtml(value)}"
          class="mt-2 block w-full rounded-xl border-gray-300 text-sm shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
          data-grade="${this.escapeHtml(field.id)}"
          data-action="input->pick-lab-strategy-builder#updateGradeMix"
        >
      </label>
    `
  }

  renderThresholdTable(step, field) {
    const fieldId = field.id
    const rows = Object.entries(step.values[fieldId] || {}).sort((left, right) => {
      return parseInt(left[0], 10) - parseInt(right[0], 10)
    })

    return `
      <div class="md:col-span-2">
        <div class="flex items-center justify-between">
          <span class="block text-xs font-bold uppercase tracking-widest text-gray-700">${this.escapeHtml(field.label || "Round Thresholds")}</span>
          <button type="button" class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest text-gray-900 hover:bg-gray-50" data-threshold-field="${this.escapeHtml(fieldId)}" data-action="pick-lab-strategy-builder#addThreshold">Add Row</button>
        </div>
        <div class="mt-2 overflow-hidden rounded-xl border border-gray-200">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50 text-left text-xs font-bold uppercase tracking-wider text-gray-500">
              <tr>
                <th class="px-3 py-2">SKUs</th>
                <th class="px-3 py-2">Rounds From</th>
                <th class="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              ${rows.map(([skuCount, roundThreshold]) => this.renderThresholdRow(skuCount, roundThreshold, fieldId)).join("")}
            </tbody>
          </table>
        </div>
      </div>
    `
  }

  renderThresholdRow(skuCount, roundThreshold, fieldId) {
    return `
      <tr>
        <td class="px-3 py-2">
          <input
            type="number"
            min="1"
            step="1"
            value="${this.escapeHtml(skuCount)}"
            class="block w-24 rounded-lg border-gray-300 text-sm shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
            data-threshold-field="${this.escapeHtml(fieldId)}"
            data-threshold-key="${this.escapeHtml(skuCount)}"
            data-threshold-part="sku_count"
            data-action="input->pick-lab-strategy-builder#updateThreshold"
          >
        </td>
        <td class="px-3 py-2">
          <input
            type="number"
            min="1"
            step="1"
            value="${this.escapeHtml(roundThreshold)}"
            class="block w-32 rounded-lg border-gray-300 text-sm shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
            data-threshold-field="${this.escapeHtml(fieldId)}"
            data-threshold-key="${this.escapeHtml(skuCount)}"
            data-threshold-part="round_count"
            data-action="input->pick-lab-strategy-builder#updateThreshold"
          >
        </td>
        <td class="px-3 py-2 text-right">
          <button
            type="button"
            class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold uppercase tracking-widest text-red-700 hover:bg-red-100"
            data-threshold-field="${this.escapeHtml(fieldId)}"
            data-threshold-key="${this.escapeHtml(skuCount)}"
            data-action="pick-lab-strategy-builder#removeThreshold"
          >Remove</button>
        </td>
      </tr>
    `
  }

  renderGradeSelect(step, field) {
    const selectedGrades = step.values[field.id] || []
    const grades = field.options || ["B", "C", "A"]

    return `
      <div>
        <span class="block text-xs font-bold uppercase tracking-widest text-gray-700">${this.escapeHtml(field.label)}</span>
        <div class="mt-2 flex flex-wrap gap-2">
          ${grades.map((grade) => {
            const checked = selectedGrades.includes(grade)
            return `
              <label class="inline-flex items-center rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-bold text-gray-800">
                <input
                  type="checkbox"
                  class="mr-2 h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                  ${checked ? "checked" : ""}
                  data-field-id="${this.escapeHtml(field.id)}"
                  data-grade="${this.escapeHtml(grade)}"
                  data-action="pick-lab-strategy-builder#toggleGrade"
                >
                ${this.escapeHtml(grade)}
              </label>
            `
          }).join("")}
        </div>
        ${field.control === "ordered_multiselect" ? `<p class="mt-2 text-xs font-semibold text-gray-500" data-grade-order="${this.escapeHtml(step.uid)}-${this.escapeHtml(field.id)}"></p>` : ""}
      </div>
    `
  }

  renderNumberField(step, field) {
    const value = step.values[field.id] ?? field.default ?? ""

    return `
      <label class="block">
        <span class="block text-xs font-bold uppercase tracking-widest text-gray-700">${this.escapeHtml(field.label)}</span>
        <input
          type="number"
          min="${this.escapeHtml(field.min ?? 0)}"
          max="${this.escapeHtml(field.max ?? "")}"
          step="${this.escapeHtml(field.step ?? 1)}"
          value="${this.escapeHtml(value)}"
          class="mt-2 block w-full rounded-xl border-gray-300 text-sm shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
          data-field-id="${this.escapeHtml(field.id)}"
          data-action="input->pick-lab-strategy-builder#updateNumber"
        >
      </label>
    `
  }

  updatePayloadAndValidation() {
    const payload = this.buildPayload()
    const messages = this.currentValidationMessages()

    this.payloadTarget.value = JSON.stringify(payload)

    if (messages.length > 0) {
      this.validationTarget.className = "rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-sm font-semibold text-gray-800"
      this.validationTarget.innerHTML = messages.map((message) => `<p>${this.escapeHtml(message)}</p>`).join("")
    } else {
      this.validationTarget.className = "rounded-xl border border-green-200 bg-green-50 p-4 text-sm font-semibold text-gray-800"
      this.validationTarget.innerHTML = "<p>Ready to save.</p>"
    }
  }

  buildPayload() {
    return {
      version: 1,
      steps: this.steps.map((step) => this.stepPayload(step)),
      defaults: {
        allow_roundup_when_exact_quantity_fails: false,
        allow_partial_fill: false
      }
    }
  }

  stepPayload(step) {
    const payload = {
      type: step.type,
      enabled: step.enabled
    }

    if (step.type === "grade_mix") {
      payload.targets = this.normalizedGradeTargets(step)
      return payload
    }

    ;(step.option.fields || []).forEach((field) => {
      if (field.id === "enabled") return

      if (field.control === "threshold_table") {
        payload[field.id] = this.normalizedThresholds(step, field.id)
      } else if (field.control === "multiselect" || field.control === "ordered_multiselect") {
        payload[field.id] = step.values[field.id] || []
      } else {
        payload[field.id] = this.numberValue(step.values[field.id])
      }
    })

    return payload
  }

  currentValidationMessages() {
    const messages = []

    if (this.steps.length === 0) {
      messages.push("Add at least one strategy rule.")
    }

    this.steps.forEach((step) => {
      if (step.enabled && step.type === "grade_mix" && this.gradeTotal(step) !== 100) {
        messages.push(`${step.name} grade percentages must total 100%. Current total: ${this.gradeTotal(step)}%.`)
      }

      ;(step.option.fields || []).forEach((field) => {
        if (step.enabled && field.control === "threshold_table") {
          this.thresholdValidationMessages(step, field).forEach((message) => messages.push(message))
        }
      })
    })

    return messages
  }

  thresholdValidationMessages(step, field) {
    const rows = Object.entries(step.values[field.id] || {}).sort((left, right) => {
      return parseInt(left[0], 10) - parseInt(right[0], 10)
    })
    const messages = []

    if (rows.length === 0) {
      messages.push(`${step.name} ${field.label} needs at least one row.`)
      return messages
    }

    rows.forEach(([skuCount, roundThreshold]) => {
      if ((parseInt(skuCount, 10) || 0) <= 0) {
        messages.push(`${step.name} ${field.label} SKU counts must be positive integers.`)
      }
      if ((parseFloat(roundThreshold) || 0) <= 0) {
        messages.push(`${step.name} ${field.label} round thresholds must be greater than zero.`)
      }
    })

    rows.slice(1).forEach((row, index) => {
      const previousThreshold = parseFloat(rows[index][1])
      const currentThreshold = parseFloat(row[1])
      if (currentThreshold <= previousThreshold) {
        messages.push(`${step.name} ${field.label} round thresholds must increase as SKU counts increase.`)
      }
    })

    return [...new Set(messages)]
  }

  refreshGradeTotals() {
    this.steps.filter((step) => step.type === "grade_mix").forEach((step) => {
      const totalTarget = this.element.querySelector(`[data-grade-total="${step.uid}"]`)
      if (!totalTarget) return

      totalTarget.textContent = `${this.gradeTotal(step)}% total`
    })
  }

  refreshGradeOrders() {
    this.steps.forEach((step) => {
      ;(step.option.fields || []).forEach((field) => {
        if (field.control !== "ordered_multiselect") return

        const target = this.element.querySelector(`[data-grade-order="${step.uid}-${field.id}"]`)
        if (!target) return

        const grades = step.values[field.id] || []
        target.textContent = grades.length > 0 ? `Selected order: ${grades.join(", ")}` : "No preferred grades selected."
      })
    })
  }

  reorderStep(draggedUid, targetUid, position = "before") {
    if (!draggedUid || !targetUid || draggedUid === targetUid) return false

    const draggedIndex = this.stepIndex(draggedUid)
    const targetIndex = this.stepIndex(targetUid)
    if (draggedIndex < 0 || targetIndex < 0) return false

    let insertionIndex = targetIndex + (position === "after" ? 1 : 0)
    const [draggedStep] = this.steps.splice(draggedIndex, 1)
    if (draggedIndex < insertionIndex) insertionIndex -= 1

    this.steps.splice(Math.max(0, Math.min(insertionIndex, this.steps.length)), 0, draggedStep)
    return true
  }

  clearPointerDrag() {
    window.removeEventListener("pointermove", this.boundPointerMove)
    window.removeEventListener("pointerup", this.boundPointerUp)
    window.removeEventListener("pointercancel", this.boundPointerUp)
    this.pointerDrag = null
    this.draggedStepUid = null
    this.markDraggingStep(null)
  }

  markDraggingStep(stepUid) {
    if (!this.hasStepListTarget) return

    this.stepListTarget.querySelectorAll("[data-step-uid]").forEach((element) => {
      const dragging = stepUid && element.dataset.stepUid === stepUid
      element.classList.toggle("opacity-60", dragging)
      element.classList.toggle("ring-2", dragging)
      element.classList.toggle("ring-yellow-400", dragging)
      element.classList.toggle("ring-offset-2", dragging)
      element.classList.toggle("shadow-xl", dragging)
      element.classList.toggle("cursor-grabbing", dragging)
    })
  }

  moveDraggingStepForEvent(event, targetStep) {
    const draggingElement = this.draggingStepElement()
    if (!draggingElement || !targetStep || targetStep === draggingElement) return false

    const rect = targetStep.getBoundingClientRect()
    const position = event.clientY > rect.top + rect.height / 2 ? "after" : "before"
    if (position === "before") {
      targetStep.before(draggingElement)
    } else {
      targetStep.after(draggingElement)
    }

    return true
  }

  draggingStepElement() {
    if (!this.hasStepListTarget || !this.draggedStepUid) return null

    return Array.from(this.stepListTarget.querySelectorAll("[data-step-uid]")).find((element) => {
      return element.dataset.stepUid === this.draggedStepUid
    })
  }

  syncStepsFromDomOrder() {
    if (!this.hasStepListTarget) return

    const orderedUids = Array.from(this.stepListTarget.querySelectorAll("[data-step-uid]")).map((element) => {
      return element.dataset.stepUid
    })
    if (orderedUids.length !== this.steps.length) return

    const stepByUid = new Map(this.steps.map((step) => [step.uid, step]))
    const orderedSteps = orderedUids.map((uid) => stepByUid.get(uid)).filter(Boolean)
    if (orderedSteps.length === this.steps.length) {
      this.steps = orderedSteps
    }
  }

  moveStep(event, direction) {
    const step = this.stepFromEvent(event)
    if (!step) return

    const index = this.stepIndex(step.uid)
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= this.steps.length) return

    const [movedStep] = this.steps.splice(index, 1)
    this.steps.splice(newIndex, 0, movedStep)
    this.render()
  }

  gripIcon() {
    return `
      <svg aria-hidden="true" viewBox="0 0 20 20" class="h-4 w-4" fill="currentColor">
        <circle cx="7" cy="5" r="1.5"></circle>
        <circle cx="13" cy="5" r="1.5"></circle>
        <circle cx="7" cy="10" r="1.5"></circle>
        <circle cx="13" cy="10" r="1.5"></circle>
        <circle cx="7" cy="15" r="1.5"></circle>
        <circle cx="13" cy="15" r="1.5"></circle>
      </svg>
    `
  }

  buildStep(option) {
    return {
      uid: this.newStepUid(),
      type: option.id,
      name: option.name,
      description: option.description,
      enabled: option.defaults?.enabled !== false,
      values: this.defaultValues(option),
      option: option
    }
  }

  initialSteps() {
    if (!this.hasInitialStrategyValue) return []

    const payload = this.initialStrategyValue.payload || {}
    return this.stepsFromPayload(payload, false)
  }

  stepsFromPayload(payload, strict) {
    return (payload.steps || []).map((payloadStep) => {
      const option = this.optionById(payloadStep.type)
      if (!option) {
        if (strict) throw new Error(`Unknown strategy rule: ${payloadStep.type}`)

        return null
      }

      const step = this.buildStep(option)
      step.enabled = payloadStep.enabled !== false
      step.values = this.valuesFromPayload(option, payloadStep)
      return step
    }).filter(Boolean)
  }

  strategyFromAgentMarkdown(markdown) {
    const parsed = this.parseAgentMarkdown(markdown)
    const source = parsed.strategy || parsed.pick_lab_strategy || parsed
    const payloadSource = source.payload || source.normalized_payload || source.strategy_payload || source

    return {
      name: source.name,
      description: source.description,
      status: source.status,
      payload: this.payloadFromAgentSource(payloadSource)
    }
  }

  parseAgentMarkdown(markdown) {
    const text = String(markdown || "").trim()
    if (text.length === 0) throw new Error("Paste the agent markdown output first.")

    const candidates = []
    const fencedJson = text.match(/```(?:json)?\s*([\s\S]*?)```/i)
    if (fencedJson) candidates.push(fencedJson[1].trim())

    const objectText = this.firstJsonObject(text)
    if (objectText) candidates.push(objectText)

    candidates.push(text)

    for (const candidate of candidates) {
      try {
        return JSON.parse(candidate)
      } catch (_error) {
        // Try the next candidate.
      }
    }

    throw new Error("Could not find valid JSON in the agent markdown. Ask the agent for one fenced json block.")
  }

  firstJsonObject(text) {
    const start = text.search(/[\[{]/)
    if (start < 0) return null

    const opener = text[start]
    const closer = opener === "{" ? "}" : "]"
    let depth = 0
    let inString = false
    let escaped = false

    for (let index = start; index < text.length; index += 1) {
      const char = text[index]

      if (inString) {
        if (escaped) {
          escaped = false
        } else if (char === "\\") {
          escaped = true
        } else if (char === "\"") {
          inString = false
        }
        continue
      }

      if (char === "\"") {
        inString = true
      } else if (char === opener) {
        depth += 1
      } else if (char === closer) {
        depth -= 1
        if (depth === 0) return text.slice(start, index + 1)
      }
    }

    return null
  }

  payloadFromAgentSource(source) {
    if (Array.isArray(source)) {
      return this.normalizedPayload(source)
    }

    if (!source || typeof source !== "object") {
      throw new Error("Agent JSON must be an object with a payload or steps array.")
    }

    if (Array.isArray(source.steps)) {
      return this.normalizedPayload(source.steps, source.defaults)
    }

    return this.normalizedPayload(this.stepsFromObjectPayload(source), source.defaults)
  }

  normalizedPayload(steps, defaults = {}) {
    return {
      version: 1,
      steps: steps,
      defaults: {
        allow_roundup_when_exact_quantity_fails: false,
        allow_partial_fill: false,
        ...defaults
      }
    }
  }

  stepsFromObjectPayload(source) {
    const steps = []
    const metadataKeys = ["version", "defaults", "name", "description", "status"]

    Object.entries(source).forEach(([rawKey, rawValue]) => {
      const key = String(rawKey)
      if (metadataKeys.includes(key)) return

      if (key === "enforce_max_sku_diversity") {
        this.applySkuDiversityCeiling(steps, rawValue)
        return
      }

      steps.push(this.payloadStepFromObjectEntry(key, rawValue))
    })

    return steps
  }

  payloadStepFromObjectEntry(rawKey, rawValue) {
    const type = this.stepTypeForKey(rawKey)
    if (!this.optionById(type)) throw new Error(`Unknown strategy rule: ${rawKey}`)

    const settings = this.plainObject(rawValue) ? rawValue : {}
    const step = {
      type: type,
      enabled: this.enabledFromValue(rawValue, settings)
    }

    if (type === "grade_mix") {
      step.targets = this.agentGradeTargets(settings.targets || settings)
      return step
    }

    if (type === "sku_diversity") {
      step.thresholds = this.agentThresholds(settings, "sku_diversity", "thresholds")
      if (settings.ceilings) step.ceilings = this.agentThresholds(settings.ceilings, "enforce_max_sku_diversity", "ceilings")
      step.enforce_max_sku_diversity = this.booleanValue(settings.enforce_max_sku_diversity)
      return step
    }

    ;(this.optionById(type).fields || []).forEach((field) => {
      if (field.id === "enabled") return

      if (field.id === "minimum_margin_percent") {
        step[field.id] = this.numberValue(settings.minimum_margin_percent ?? settings.minimum_margin_to_consider ?? rawValue)
      } else if (field.control === "threshold_table") {
        step[field.id] = this.agentThresholds(settings[field.id] || settings, type, field.id)
      } else if (field.control === "multiselect" || field.control === "ordered_multiselect") {
        step[field.id] = Array.isArray(settings[field.id]) ? settings[field.id] : []
      } else {
        step[field.id] = this.numberValue(settings[field.id] ?? rawValue)
      }
    })

    return step
  }

  stepTypeForKey(key) {
    return {
      grade: "grade_mix",
      minimum_margin_percent: "minimum_margin_to_consider"
    }[key] || key
  }

  applySkuDiversityCeiling(steps, rawValue) {
    if (this.plainObject(rawValue)) {
      steps.push(this.payloadStepFromObjectEntry("enforce_max_sku_diversity", rawValue))
      return
    }

    let step = steps.find((candidate) => candidate.type === "sku_diversity")
    if (!step) {
      step = this.payloadStepFromObjectEntry("sku_diversity", true)
      steps.push(step)
    }

    step.enforce_max_sku_diversity = this.booleanValue(rawValue)
  }

  agentGradeTargets(source) {
    const defaults = this.optionById("grade_mix")?.defaults?.targets || { B: 50, C: 25, A: 25 }
    const targets = {}

    ;["B", "C", "A"].forEach((grade) => {
      targets[grade] = this.numberValue(source[grade] ?? source[grade.toLowerCase()] ?? defaults[grade])
    })

    return targets
  }

  agentThresholds(source, optionId = "sku_diversity", fieldId = "thresholds") {
    const thresholdSource = source[fieldId] || source.thresholds || source
    const thresholds = Object.entries(thresholdSource).reduce((result, [key, value]) => {
      if (/^\d+$/.test(String(key))) result[String(parseInt(key, 10))] = this.numberValue(value)

      return result
    }, {})

    return Object.keys(thresholds).length > 0 ?
      thresholds :
      { ...(this.optionById(optionId)?.defaults?.[fieldId] || {}) }
  }

  applyStrategyMetadata(strategy) {
    if (strategy.name && this.hasStrategyNameTarget) this.strategyNameTarget.value = strategy.name
    if (strategy.description && this.hasStrategyDescriptionTarget) {
      this.strategyDescriptionTarget.value = strategy.description
    }
    if (["draft", "published", "approved"].includes(strategy.status) && this.hasStrategyStatusTarget) {
      this.strategyStatusTarget.value = strategy.status
    }
  }

  showAgentMessage(message, status) {
    if (!this.hasAgentMessageTarget) return

    const success = status === "success"
    this.agentMessageTarget.className = success ?
      "mt-3 rounded-xl border border-green-200 bg-green-50 p-3 text-sm font-semibold text-green-800" :
      "mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-800"
    this.agentMessageTarget.textContent = message
  }

  plainObject(value) {
    return value && typeof value === "object" && !Array.isArray(value)
  }

  enabledFromValue(rawValue, settings) {
    if (Object.prototype.hasOwnProperty.call(settings, "enabled")) return this.booleanValue(settings.enabled)
    if (this.plainObject(rawValue)) return true

    return this.booleanValue(rawValue)
  }

  booleanValue(value) {
    if (value === undefined || value === null) return false
    if (typeof value === "boolean") return value
    if (typeof value === "number") return value !== 0

    return !["false", "0", "no", "off"].includes(String(value).toLowerCase())
  }

  valuesFromPayload(option, payloadStep) {
    const values = this.defaultValues(option)

    if (option.id === "grade_mix") {
      values.targets = {
        ...values.targets,
        ...(payloadStep.targets || {})
      }
      return values
    }

    ;(option.fields || []).forEach((field) => {
      if (field.id === "enabled" || payloadStep[field.id] === undefined) return

      if (field.control === "threshold_table") {
        values[field.id] = { ...payloadStep[field.id] }
      } else if (field.control === "multiselect" || field.control === "ordered_multiselect") {
        values[field.id] = [...payloadStep[field.id]]
      } else {
        values[field.id] = payloadStep[field.id]
      }
    })

    return values
  }

  defaultValues(option) {
    if (option.id === "grade_mix") {
      return {
        targets: { ...(option.defaults?.targets || { B: 50, C: 25, A: 25 }) }
      }
    }

    return (option.fields || []).reduce((values, field) => {
      if (field.id === "enabled") return values

      if (field.control === "threshold_table") {
        values[field.id] = { ...(field.default || option.defaults?.[field.id] || {}) }
      } else if (field.control === "multiselect" || field.control === "ordered_multiselect") {
        values[field.id] = [...(field.default || option.defaults?.[field.id] || [])]
      } else {
        values[field.id] = option.defaults?.[field.id] ?? field.default ?? ""
      }

      return values
    }, {})
  }

  normalizedGradeTargets(step) {
    return ["B", "C", "A"].reduce((targets, grade) => {
      targets[grade] = this.numberValue(step.values.targets[grade])
      return targets
    }, {})
  }

  normalizedThresholds(step, fieldId = "thresholds") {
    return Object.entries(step.values[fieldId] || {})
      .sort((left, right) => parseInt(left[0], 10) - parseInt(right[0], 10))
      .reduce((thresholds, [skuCount, roundThreshold]) => {
        thresholds[String(parseInt(skuCount, 10) || 1)] = this.numberValue(roundThreshold)
        return thresholds
      }, {})
  }

  gradeTotal(step) {
    return Object.values(this.normalizedGradeTargets(step)).reduce((sum, value) => sum + value, 0)
  }

  numberValue(value) {
    const number = parseFloat(value)
    if (!Number.isFinite(number)) return 0

    return Number.isInteger(number) ? parseInt(number, 10) : number
  }

  availableOptions() {
    return (this.optionsValue.options || []).filter((option) => option.available !== false)
  }

  optionById(id) {
    return (this.optionsValue.options || []).find((option) => option.id === id)
  }

  stepFromEvent(event) {
    const element = event.currentTarget.closest("[data-step-uid]")
    if (!element) return null

    return this.steps.find((step) => step.uid === element.dataset.stepUid)
  }

  stepIndex(uid) {
    return this.steps.findIndex((step) => step.uid === uid)
  }

  newStepUid() {
    const uid = `step-${Date.now()}-${this.nextStepId}`
    this.nextStepId += 1
    return uid
  }

  escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }
}
